import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, HUB_META } from "../lib/api";
import AppShell from "../components/AppShell";
import VoiceRecorder from "../components/VoiceRecorder";
import { useAuth } from "../lib/auth";
import { Image as ImageIcon, Microphone, VideoCamera, ChatText, X, ArrowRight } from "@phosphor-icons/react";

const TYPES = [
  { v: "text", l: "Text", icon: ChatText },
  { v: "image", l: "Image", icon: ImageIcon },
  { v: "audio", l: "Voice", icon: Microphone },
  { v: "video", l: "Video", icon: VideoCamera },
];

export default function Compose() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);

  const [hubs, setHubs] = useState([]);
  const [type, setType] = useState("text");
  const [hubKey, setHubKey] = useState("aerospace");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("");

  useEffect(() => { api.get("/hubs").then((r) => setHubs(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (user === false) nav("/login", { state: { from: "/compose" } });
  }, [user, nav]);

  const reset = () => {
    setImageFile(null);
    setMediaBlob(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!title.trim()) return setError("Title is required.");
    setBusy(true);
    try {
      let media_url = "";
      let media_type = "none";

      if (type === "image" && imageFile) {
        setStage("Uploading image...");
        const fd = new FormData();
        fd.append("file", imageFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        media_url = data.url;
        media_type = "image";
      } else if ((type === "audio" || type === "video") && mediaBlob) {
        setStage("Uploading media...");
        const fd = new FormData();
        const ext = type === "audio" ? "webm" : "webm";
        fd.append("file", new File([mediaBlob], `${type}-${Date.now()}.${ext}`, { type: mediaBlob.type }));
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        media_url = data.url;
        media_type = type;
      }

      setStage(media_type === "audio" || media_type === "video" ? "Transcribing with AI..." : "Publishing...");
      const { data: post } = await api.post("/posts", {
        hub_key: hubKey,
        title,
        body,
        media_type,
        media_url,
      });
      nav(`/post/${post.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
      setStage("");
    }
  };

  if (!user || user === false) {
    return <AppShell><div className="py-20 text-center label-mono">CHECKING SESSION...</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="compose-page">
        <div className="label-mono mb-2" style={{ color: "#E60012" }}>NEW POST</div>
        <h1 className="font-display text-white mb-8" style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-0.035em" }}>
          Drop a signal.
        </h1>

        <form onSubmit={handleSubmit} data-testid="compose-form">
          {/* Type selector */}
          <div className="label-mono mb-2">FORMAT</div>
          <div className="grid grid-cols-4 gap-2 mb-6">
            {TYPES.map((t) => (
              <button type="button" key={t.v} onClick={() => { setType(t.v); reset(); }}
                className={`p-4 border flex flex-col items-center gap-2 transition-colors ${
                  type === t.v ? "bg-[#E60012] border-[#E60012] text-white" : "border-[#27272A] text-zinc-400 hover:border-zinc-500"
                }`}
                data-testid={`compose-type-${t.v}`}
              >
                <t.icon size={20} weight={type === t.v ? "fill" : "regular"} />
                <span className="label-mono" style={{ fontSize: 9, color: "inherit" }}>{t.l}</span>
              </button>
            ))}
          </div>

          {/* Hub selector */}
          <div className="label-mono mb-2">HUB</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
            {hubs.map((h) => {
              const color = HUB_META[h.key]?.color || h.color || "#E60012";
              return (
                <button type="button" key={h.key} onClick={() => setHubKey(h.key)}
                  className={`p-3 border text-left transition-colors ${
                    hubKey === h.key ? "border-white bg-[#1A1A1D]" : "border-[#27272A] hover:border-zinc-500"
                  }`}
                  data-testid={`compose-hub-${h.key}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="hub-dot" style={{ color }} />
                    <span className="label-mono" style={{ color, fontSize: 9 }}>{(HUB_META[h.key]?.short || h.key.toUpperCase().slice(0, 3))}</span>
                  </div>
                  <div className="text-xs text-white">{h.name}</div>
                </button>
              );
            })}
          </div>

          {/* Title */}
          <div className="label-mono mb-2">TITLE</div>
          <input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={160}
            className="input mb-6" placeholder="One sharp sentence." data-testid="compose-title-input" />

          {/* Body */}
          <div className="label-mono mb-2">BODY {type !== "text" ? "(OPTIONAL)" : ""}</div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5}
            className="input mb-6 resize-none" placeholder="Context, data, opinion..." data-testid="compose-body-input" />

          {/* Media */}
          {type === "image" && (
            <div className="mb-6">
              <div className="label-mono mb-2">IMAGE</div>
              <input ref={fileRef} type="file" accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                className="block text-sm text-zinc-300 file:mr-3 file:py-2 file:px-4 file:border file:border-[#27272A] file:bg-[#121214] file:text-white file:font-mono file:text-xs file:uppercase file:tracking-wider hover:file:bg-[#1A1A1D]"
                data-testid="compose-image-input"
              />
              {imageFile && (
                <div className="mt-3 flex items-center gap-3 text-sm text-zinc-400">
                  <span>{imageFile.name}</span>
                  <button type="button" onClick={() => { setImageFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                    className="btn btn-ghost btn-sm" data-testid="compose-image-clear">
                    <X size={12} />
                  </button>
                </div>
              )}
            </div>
          )}

          {(type === "audio" || type === "video") && (
            <div className="mb-6">
              <div className="label-mono mb-2">{type === "audio" ? "VOICE NOTE" : "VIDEO"}</div>
              <VoiceRecorder
                accept={type}
                onRecorded={(blob) => setMediaBlob(blob)}
                onClear={() => setMediaBlob(null)}
              />
            </div>
          )}

          {error && (
            <div className="mb-4 text-sm text-red-500 border border-red-900/50 bg-red-950/30 px-3 py-2" data-testid="compose-error">
              {error}
            </div>
          )}

          {busy && stage && (
            <div className="mb-4 text-sm text-zinc-300 border border-[#27272A] px-3 py-2 flex items-center gap-2" data-testid="compose-stage">
              <span className="spinner" /> {stage}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button type="button" onClick={() => nav(-1)} className="btn btn-ghost" data-testid="compose-cancel-btn">Cancel</button>
            <button type="submit" disabled={busy} className="btn btn-primary" data-testid="compose-publish-btn">
              {busy ? <span className="spinner" /> : <>Publish <ArrowRight size={13} weight="bold" /></>}
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
